// src/components/SuperAdmin/StatsCard.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { HiOutlineArrowUp, HiOutlineArrowDown, HiOutlineTrendingUp } from 'react-icons/hi';

const StatsCard = ({ 
  title, 
  value, 
  icon: Icon, 
  color = 'gray', 
  onClick,
  trend = null, // { value: 12, direction: 'up' or 'down', label: 'vs last month' }
  subtitle = 'Last 30 days',
  loading = false,
  badge = null
}) => {
  
  const colorVariants = {
    gray: {
      bg: 'from-gray-700 to-gray-800',
      border: 'border-gray-600',
      iconBg: 'bg-gray-600',
      iconBorder: 'border-gray-500',
      text: 'text-gray-200'
    },
    blue: {
      bg: 'from-blue-600 to-blue-700',
      border: 'border-blue-500',
      iconBg: 'bg-blue-500',
      iconBorder: 'border-blue-400',
      text: 'text-blue-100'
    },
    green: {
      bg: 'from-green-600 to-green-700',
      border: 'border-green-500',
      iconBg: 'bg-green-500',
      iconBorder: 'border-green-400',
      text: 'text-green-100'
    },
    purple: {
      bg: 'from-purple-600 to-purple-700',
      border: 'border-purple-500',
      iconBg: 'bg-purple-500',
      iconBorder: 'border-purple-400',
      text: 'text-purple-100'
    },
    red: {
      bg: 'from-red-600 to-red-700',
      border: 'border-red-500',
      iconBg: 'bg-red-500',
      iconBorder: 'border-red-400',
      text: 'text-red-100'
    },
    yellow: {
      bg: 'from-yellow-600 to-yellow-700',
      border: 'border-yellow-500',
      iconBg: 'bg-yellow-500',
      iconBorder: 'border-yellow-400',
      text: 'text-yellow-100'
    }
  };

  const theme = colorVariants[color] || colorVariants.gray;

  // Loading skeleton
  if (loading) {
    return (
      <div className={`bg-gradient-to-br ${theme.bg} rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 md:p-6 border ${theme.border} animate-pulse`}>
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="h-3 bg-white/20 rounded w-20 mb-2"></div>
            <div className="h-6 bg-white/20 rounded w-16"></div>
          </div>
          <div className={`${theme.iconBg} p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl ml-2`}>
            <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8"></div>
          </div>
        </div>
        <div className="mt-2 sm:mt-3">
          <div className="h-3 bg-white/20 rounded w-24"></div>
        </div>
      </div>
    );
  }

  const formattedValue = typeof value === 'number' ? value.toLocaleString() : value;

  return (
    <motion.div
      whileHover={{ 
        y: -8, 
        scale: 1.02,
        transition: { type: "spring", stiffness: 400, damping: 10 }
      }}
      whileTap={{ scale: 0.98 }}
      className={`bg-gradient-to-br ${theme.bg} rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 md:p-6 cursor-pointer transition-all border ${theme.border} relative overflow-hidden group`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && onClick()}
      aria-label={`${title} card, value: ${formattedValue}`}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <pattern id="grid" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
          </pattern>
          <rect x="0" y="0" width="100" height="100" fill="url(#grid)" />
        </svg>
      </div>

      {/* Glow effect on hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

      <div className="relative z-10">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className={`${theme.text} text-xs sm:text-sm font-medium truncate opacity-80`}>
              {title}
            </p>
            <div className="flex items-center gap-2 mt-1 sm:mt-2">
              <p className="text-white text-xl sm:text-2xl md:text-3xl font-bold truncate">
                {formattedValue}
              </p>
              {badge && (
                <span className="px-1.5 py-0.5 bg-white/20 rounded-full text-xs text-white">
                  {badge}
                </span>
              )}
            </div>
          </div>
          
          <motion.div 
            whileHover={{ rotate: 10, scale: 1.1 }}
            className={`${theme.iconBg} p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl ml-2 flex-shrink-0 border ${theme.iconBorder} shadow-lg group-hover:shadow-xl transition-all`}
          >
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
          </motion.div>
        </div>

        {/* Trend indicator */}
        <div className="mt-2 sm:mt-3 flex items-center justify-between">
          <div className="flex items-center text-white/60 text-xs">
            <HiOutlineTrendingUp className="w-3 h-3 mr-1" />
            <span>{subtitle}</span>
          </div>

          {trend && (
            <motion.div 
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                trend.direction === 'up' 
                  ? 'bg-green-500/20 text-green-300' 
                  : 'bg-red-500/20 text-red-300'
              }`}
            >
              {trend.direction === 'up' ? (
                <HiOutlineArrowUp className="w-3 h-3" />
              ) : (
                <HiOutlineArrowDown className="w-3 h-3" />
              )}
              <span>{Math.abs(trend.value)}%</span>
              {trend.label && <span className="opacity-75 ml-1">{trend.label}</span>}
            </motion.div>
          )}
        </div>

        {/* Progress bar (optional) */}
        {trend && (
          <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(Math.abs(trend.value), 100)}%` }}
              transition={{ duration: 1, delay: 0.2 }}
              className={`h-full ${
                trend.direction === 'up' ? 'bg-green-400' : 'bg-red-400'
              }`}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default StatsCard;